import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Monday-style full-cell status. The whole <td> is tinted with the label's
// color; the label text sits centered in white. Empty state shows a neutral
// gray cell with "—". Clicking opens a swatch picker.
//
// The picker renders via a portal to <body> with fixed positioning relative
// to the trigger's viewport rect. This escapes any ancestor stacking context
// or `overflow` clip (sticky headers, table-wrappers, group-blocks, etc.)
// that would otherwise hide the top/bottom of the menu when the cell sits
// near a clipped boundary.
export default function StatusCell({ value, column, readOnly, onCommit }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, placement: "below" });

  const labels = column?.settings?.labels || [];
  const current = labels.find((l) => l.id === value) || null;

  const pick = (labelId) => {
    setOpen(false);
    if (labelId !== value) onCommit(labelId);
  };

  // Position the menu next to the trigger every time it opens (or the window
  // resizes/scrolls). If there isn't room below, flip above. `useLayoutEffect`
  // runs before paint so the first frame already has correct coordinates.
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const btn = btnRef.current;
      const menu = menuRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const menuH = menu?.offsetHeight || 240; // conservative default before first measure
      const menuW = menu?.offsetWidth || 220;
      const vpH = window.innerHeight;
      const vpW = window.innerWidth;
      const spaceBelow = vpH - r.bottom;
      const placement = spaceBelow >= menuH + 8 || spaceBelow >= r.top
        ? "below"
        : "above";
      const top = placement === "below"
        ? Math.min(r.bottom + 4, vpH - menuH - 8)
        : Math.max(8, r.top - menuH - 4);
      const left = Math.min(Math.max(8, r.left), vpW - menuW - 8);
      setPos({ top, left, placement });
    };
    update();
    // Recompute on scroll/resize. Capture phase so scroll on inner wrappers
    // (table wrap, page main) also triggers it.
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  // Close on Escape — nicer than only the backdrop click.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`cell-display status-full ${current ? "has-value" : "empty"} ${readOnly ? "read-only" : ""}`}
        style={{ background: current?.color || undefined }}
        disabled={readOnly}
        onClick={() => !readOnly && setOpen((o) => !o)}
      >
        <span className="status-label">{current?.label || "—"}</span>
      </button>

      {open &&
        createPortal(
          <>
            <div
              className="cell-menu-backdrop"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
            />
            <div
              ref={menuRef}
              className="cell-menu status-menu status-menu-portal"
              style={{ top: pos.top, left: pos.left }}
              onClick={(e) => e.stopPropagation()}
            >
              {labels.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  className="status-menu-item"
                  style={{ background: l.color, color: "#fff" }}
                  onClick={() => pick(l.id)}
                >
                  {l.label}
                </button>
              ))}
              {value && (
                <button
                  type="button"
                  className="status-menu-item status-menu-clear"
                  onClick={() => pick(null)}
                >
                  Clear
                </button>
              )}
            </div>
          </>,
          document.body
        )}
    </>
  );
}
